import React, { useEffect, useRef, useState } from "react";
import {
  Camera,
  Tag,
  Layout,
  Heart,
  MessageCircle,
  ShieldCheck,
  PlusCircle,
  MoreHorizontal,
  ArrowLeft,
  Truck,
} from "lucide-react";
import { ref, onValue, push, set, get, update } from "firebase/database";
import { db, auth } from "../firebase";
import {
  getStorage,
  ref as sRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import "../pages/passenger.css";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../utils/formatCurrency";



const SellerMarketOrders = ({ closePopup }) => {
  const { t } = useTranslation();  
  const [orders, setOrders] = useState([]);
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;

    const ordersRef = ref(db, "marketOrders");

    const unsubscribe = onValue(ordersRef, (snap) => {
      const data = snap.val() || {};

      const sellerOrders = Object.values(data)
        .filter((order) => order.sellerId === currentUser.uid)
        .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));

      setOrders(sellerOrders);
    });

    return () => unsubscribe();
  }, [currentUser]);

  if (orders.length === 0) return null;

  return (
    <div className="social-card">
      <h3 style={{ marginTop: 0, color: "#0a4ecc" }}>
{`📦 ${t("seller_orders")}`}        
      </h3>

      {orders.map((order) => (
        <div
          key={order.orderId}
          style={{
            border: "1px solid #eee",
            borderRadius: "10px",
            padding: "10px",
            marginBottom: "8px",
            background: "#f8f9ff",
          }}
        >
<b
  style={{
    color: "#111",
    fontSize: "12px",
    lineHeight: "16px",
    display: "block",
  }}
>
  {order.itemTitle}
</b>          

         <p
  style={{
    margin: "5px 0",
    fontSize: "12px",
    color: "#222",
    fontWeight: "500",
  }}
>
{t("buyer")}: {order.buyerName || t("buyer")}  
</p>

         <p
  style={{
    margin: "5px 0",
    fontSize: "12px",
    color: "#222",
    fontWeight: "500",
  }}
>
{t("status")}:  
  <b style={{ color: "#111", marginLeft: "4px" }}>
    {order.orderStatus}
  </b>
</p>


<button
  onClick={() => {
    closePopup();
    window.location.href = `/market-chat/${order.chatId}`;
  }}
  style={{
    width: "100%",
    padding: "9px",
    border: "none",
    borderRadius: "8px",
    background: "#0a4ecc",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  }}
>
{t("accept_order")}
</button>
        </div>
      ))}
    </div>
  );
};

const Market = ({ onClose }) => {
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
const [showSellerOrders, setShowSellerOrders] = useState(false);
  const [posts, setPosts] = useState([]);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [posting, setPosting] = useState(false);
const [listingFilter, setListingFilter] = useState("all");
const userCountry =
  sellerProfile?.country ||
  localStorage.getItem("country") ||
  "Papua New Guinea";

const categories = [
  { value: "electronics", label: t("category_electronics") },
  { value: "fashion", label: t("category_fashion") },
  { value: "home", label: t("category_home") },
  { value: "food", label: t("category_food") },
  { value: "auto_parts", label: t("category_auto_parts") },
  { value: "stationery", label: t("category_stationery") },
  { value: "services", label: t("category_services") },
  { value: "other", label: t("category_other") },
];

  const getTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - Number(timestamp || 0);

    const mins = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (mins < 1) return t("just_now");
if (mins < 60) return `${mins}${t("minutes_ago")}`;
if (hours < 24) return `${hours}${t("hours_ago")}`;
return `${days}${t("days_ago")}`;

};


  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const loadProfile = async () => {
      try {
   const passengerSnap = await get(
  ref(db, `passengers/${currentUser.uid}`)
);

const driverSnap = await get(
  ref(db, `drivers/${currentUser.uid}`)
);

const businessSnap = await get(
  ref(db, `businesses/${currentUser.uid}`)
);

if (businessSnap.exists()) {
  setSellerProfile({
    role: "business",
    ...businessSnap.val(),
  });
  return;
}

if (driverSnap.exists()) {
  setSellerProfile({
    role: "driver",
    ...driverSnap.val(),
  });
  return;
}

if (passengerSnap.exists()) {
  setSellerProfile({
    role: "passenger",
    ...passengerSnap.val(),
  });
  return;
}


      } catch (error) {
        console.error("Failed to load seller profile:", error);
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    const marketRef = ref(db, "marketPosts");

    const unsubscribe = onValue(marketRef, (snapshot) => {
      const data = snapshot.val() || {};

      const loadedPosts = Object.entries(data)
        .map(([id, post]) => ({
          id,
          ...post,
        }))
        .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));

      setPosts(loadedPosts);
    });



    return () => unsubscribe();
  }, []);


const filteredPosts = posts.filter((post) => {
  if (listingFilter === "all") return true;
  if (listingFilter === "drivers") return post.sellerRole === "driver";
  if (listingFilter === "passengers") return post.sellerRole === "passenger";
  if (listingFilter === "business") return post.sellerRole === "business";
  if (listingFilter === "verified") return post.verifiedSeller === true;
  return true;
});


  const openGallery = () => {
    fileInputRef.current?.click();
  };

  const handleChoosePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedImage(file);
    setPreviewImage(URL.createObjectURL(file));
  };

  const handlePostItem = async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
alert(t("please_login_first"));      
      return;
    }

    if (!sellerProfile) {
alert(t("seller_profile_not_found"));      
      return;
    }

    if (!description.trim()) {
alert(t("enter_item_description"));      
      return;
    }

    if (!price || Number(price) <= 0) {
alert(t("enter_valid_price"));      
      return;
    }

    if (!category) {
alert(t("select_category_alert"));      
      return;
    }

    if (!selectedImage) {
alert(t("choose_product_photo"));      
      return;
    }

    setPosting(true);

    try {
      const postRef = push(ref(db, "marketPosts"));
      const postId = postRef.key;

      const storage = getStorage();
      const imageRef = sRef(
        storage,
        `marketPosts/${currentUser.uid}/${postId}/product.jpg`
      );

      await uploadBytes(imageRef, selectedImage);
      const imageUrl = await getDownloadURL(imageRef);

      const postData = {
        sellerId: currentUser.uid,
        sellerRole: sellerProfile.role || "passenger",
sellerName: sellerProfile.name || t("unknown_seller"),        
        sellerPhone: sellerProfile.phone || "",
        sellerPhoto:
          sellerProfile.photoURL ||
          sellerProfile.photoUrl ||
          "https://api.dicebear.com/7.x/avataaars/svg?seed=user",
        sellerCity: sellerProfile.city || "",
        sellerProvince: sellerProfile.province || "",
        sellerCountry: sellerProfile.country || "",
        description: description.trim(),
        price: Number(price),
        category,
        images: [imageUrl],
        likes: 0,
        comments: 0,
        createdAt: Date.now(),
        escrow: true,
        status: "active",
      };

      await set(postRef, postData);

      setDescription("");
      setPrice("");
      setCategory("");
      setSelectedImage(null);
      setPreviewImage("");

alert(t("item_posted_success"));      
    } catch (error) {
      console.error("Post item error:", error);
alert(t("failed_post_item"));

    } finally {
      setPosting(false);
    }
  };

  const handleBuyNow = async (post) => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
alert(t("please_login_first"));      

      return;
    }

    if (!post?.sellerId) {
alert(t("seller_not_found"));      
      return;
    }

    if (currentUser.uid === post.sellerId) {
alert(t("cannot_buy_own_item"));      
      return;
    }

    try {
      const chatId = [currentUser.uid, post.sellerId, post.id].join("_");

      const buyerPassengerSnap = await get(ref(db, `passengers/${currentUser.uid}`));
      const buyerDriverSnap = await get(ref(db, `drivers/${currentUser.uid}`));

      const buyerProfile = buyerPassengerSnap.exists()
        ? buyerPassengerSnap.val()
        : buyerDriverSnap.exists()
        ? buyerDriverSnap.val()
        : {};

      const normalize = (value) => String(value || "").trim().toLowerCase();

      const originCity = post.sellerCity || "";
      const originProvince = post.sellerProvince || "";
      const originCountry = post.sellerCountry || "";

      const destinationCity = buyerProfile.city || "";
      const destinationProvince = buyerProfile.province || "";
      const destinationCountry = buyerProfile.country || "";

      let deliveryType = "";
      let deliveryProvider = "";

      if (
        normalize(originCity) &&
        normalize(originCity) === normalize(destinationCity) &&
        normalize(originCountry) === normalize(destinationCountry)
      ) {
        deliveryType = "same_city";
        deliveryProvider = "kinatrip";
      } else if (
        normalize(originCountry) &&
        normalize(originCountry) === normalize(destinationCountry)
      ) {
        deliveryType = "domestic";
        deliveryProvider = "partner";
      } else {
        deliveryType = "international";
        deliveryProvider = "dhl";
      }

      const chatRef = ref(db, `marketChats/${chatId}`);
      const chatSnap = await get(chatRef);

      if (!chatSnap.exists()) {
        await set(chatRef, {
          chatId,
          postId: post.id,
          itemTitle: post.description || t("market_item"),
          itemPrice: Number(post.price || 0),
          itemImage: post.images?.[0] || "",
          buyerId: currentUser.uid,
buyerName: buyerProfile.name || t("buyer"),          
          buyerPhoto: buyerProfile.photoURL || buyerProfile.photoUrl || "",
          sellerId: post.sellerId,
sellerName: post.sellerName || t("seller"),          
          sellerPhoto: post.sellerPhoto || "",
          createdAt: Date.now(),
          updatedAt: Date.now(),
          status: "open",
        });

        const firstMessageRef = push(ref(db, `marketChatMessages/${chatId}`));
        await set(firstMessageRef, {
          senderId: currentUser.uid,
senderName: buyerProfile.name || t("buyer"),          
          text: `${t("market_buy_message")} ${
  post.description || t("market_item")
} ${t("for_price")} ${formatCurrency(
  Number(post.price || 0),
  userCountry
)}.`,

          createdAt: Date.now(),
          type: "text",
        });
      } else {
        await update(chatRef, {
          updatedAt: Date.now(),
        });
      }

      const ordersRef = ref(db, "marketOrders");
      const ordersSnap = await get(ordersRef);
      const ordersData = ordersSnap.val() || {};

      const existingOrder = Object.values(ordersData).find(
        (order) =>
          order.chatId === chatId &&
          order.postId === post.id &&
          order.buyerId === currentUser.uid &&
          order.sellerId === post.sellerId
      );

      if (!existingOrder) {
        const newOrderRef = push(ref(db, "marketOrders"));

        await set(newOrderRef, {
          orderId: newOrderRef.key,
          chatId,
          postId: post.id,
          buyerId: currentUser.uid,
buyerName: buyerProfile.name || t("buyer"),          
          sellerId: post.sellerId,
sellerName: post.sellerName || t("seller"),          
          itemTitle: post.description || t("market_item"),
          itemImage: post.images?.[0] || "",
          amount: Number(post.price || 0),
          currency: "PGK",
          originCity,
          originProvince,
          originCountry,
          destinationCity,
          destinationProvince,
          destinationCountry,
          deliveryType,
          deliveryProvider,
          escrow: true,
          paymentProvider: "stripe_or_paypal_later",
         paymentStatus: "not_paid",
orderStatus: "waiting_seller_acceptance",
deliveryStatus: "not_assigned",
escrowStatus: "not_started",
sellerAccepted: false,
driverAssigned: false,
buyerReceived: false,
escrowReleased: false,

          buyerConfirmed: false,
          sellerConfirmed: false,
          driverId: "",
          pickupVerified: false,
          deliveryVerified: false,
          releaseApproved: false,
          disputeOpened: false,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
      }

      window.location.href = `/market-chat/${chatId}`;
    } catch (error) {
      console.error("Buy Now error:", error);
alert(t("failed_open_order_chat"));      
    }
  };

  return (
    <div className="social-profile-overlay">
      <div className="social-top-nav">
        <div className="social-logo" onClick={onClose}>
          <ArrowLeft size={22} style={{ marginRight: "8px" }} />
          {t("kinamarket")}
        </div>
        <div className="social-nav-links">
         <div className="social-nav-profile">
  <ShieldCheck size={18} color="#fff" />
  <span>{t("escrow_active")}</span>
</div>

<button
  onClick={() => setShowSellerOrders((prev) => !prev)}
  style={{
    background: "#ff4757",
    color: "#fff",
    border: "none",
    borderRadius: "20px",
    padding: "7px 10px",
    fontSize: "11px",
    fontWeight: "bold",
    cursor: "pointer",
    boxShadow: "0 0 10px rgba(255,71,87,0.6)",
  }}
>
{`📦 ${t("seller_orders")}`}  
</button>
        </div>
      </div>

      <div className="social-container" style={{ maxWidth: "700px" }}>
        <div
          className="social-card"
          style={{
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            color: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "8px",
            }}
          >
            <Truck color="#f5bb41" size={24} />
            <h3 style={{ margin: 0, fontSize: "16px", color: "#f5bb41" }}>
              {t("transport_provided_by_kinatrip")}
            </h3>
          </div>

       <p style={{ margin: 0, fontSize: "13px", color: "#cbd5e1" }}>
  {t("escrow_description")}
</p>
        </div>

        <div className="social-card">
          <div className="create-post-header">{t("list_item_for_sale")}</div>

          <div className="create-post-input-area">
            <img
              src={
                sellerProfile?.photoURL ||
                sellerProfile?.photoUrl ||
                "https://api.dicebear.com/7.x/avataaars/svg?seed=user"
              }
              alt="User"
            />
            <input
  type="text"
  placeholder={t("what_are_you_selling_today")}
  value={description}
  onChange={(e) => setDescription(e.target.value)}
  style={{
    color: "#111",
    background: "#fff",
  }}
/>
          </div>

          {previewImage && (
            <div style={{ marginTop: "12px" }}>
              <img
                src={previewImage}
                alt="Preview"
                className="post-single-image"
                style={{ borderRadius: "12px" }}
              />
            </div>
          )}

          <div style={{ marginTop: "12px", display: "grid", gap: "10px" }}>
            <input
              type="number"
              placeholder={t("enter_price")}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #ddd",
                outline: "none",
              }}
            />

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #ddd",
                outline: "none",
                background: "#fff",
              }}
            >
              <option value="">{t("select_category")}</option>
          {categories.map((item) => (
  <option key={item.value} value={item.value}>
    {item.label}
  </option>
))}
            </select>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleChoosePhoto}
          />

          <div className="create-post-actions">
            <button className="post-action-btn btn-image" onClick={openGallery}>
              <Camera size={16} /> {t("photo")}
            </button>

            <button
              className="post-action-btn btn-mood"
              type="button"
              onClick={() => {}}
            >
              <Tag size={16} /> {t("price")}
            </button>

            <button
              className="post-action-btn btn-video"
              type="button"
              onClick={() => {}}
            >
              <Layout size={16} /> {t("category")}
            </button>

            <button
              className="post-action-btn btn-submit"
              style={{ flex: 1 }}
              onClick={handlePostItem}
              disabled={posting}
            >
              {posting ? t("posting") : t("post_item")}
            </button>
          </div>
        </div>



<div className="market-filter-tabs">
  <button onClick={() => setListingFilter("all")}>
    {t("all_listings")}
  </button>

  <button onClick={() => setListingFilter("drivers")}>
    {t("driver_listings")}
  </button>

  <button onClick={() => setListingFilter("passengers")}>
    {t("passenger_listings")}
  </button>

  <button onClick={() => setListingFilter("business")}>
    {t("business_listings")}
  </button>

  <button onClick={() => setListingFilter("verified")}>
    {t("verified_sellers")}
  </button>
</div>


{showSellerOrders && (
  <div
    style={{
      position: "fixed",
      top: "58px",
      right: "10px",
      width: "92%",
      maxWidth: "360px",
      maxHeight: "70vh",
      overflowY: "auto",
      background: "#fff",
      borderRadius: "14px",
      zIndex: 9999,
      boxShadow: "0 8px 25px rgba(0,0,0,0.25)",
      padding: "10px",
    }}
  >
<SellerMarketOrders
  closePopup={() => setShowSellerOrders(false)}
/>
  </div>
)}

        <h3 style={{ fontSize: "18px", color: "#333", margin: "10px 0 0 5px" }}>
          {t("trending_items")}
        </h3>

        {posts.length === 0 ? (
          <div className="social-card">
            <p style={{ margin: 0, color: "#666" }}>{t("no_market_items_yet")}</p>
          </div>
        ) : (
filteredPosts.map((post) => (          
            <div key={post.id} className="social-card">
              <div className="post-header">
                <div className="post-user-info">
                  <img src={post.sellerPhoto} alt="Avatar" />
                  <div>
                    <div className="post-user-name">
{post.sellerName || t("unknown_seller")}                      
                    </div>
                    <div className="post-time">
{getTimeAgo(post.createdAt)} • {t("secure_escrow")}                      
                    </div>
                  </div>
                </div>
                <MoreHorizontal color="#888" cursor="pointer" />
              </div>

              <div className="post-text">
                {post.description}

                <span style={{ display: "block", marginTop: "6px", fontWeight: "bold" }}>
                  {formatCurrency(Number(post.price || 0), userCountry)}
                </span>

                <div style={{ fontSize: "12px", color: "#888", marginTop: "5px" }}>
                  📍 {post.sellerCity || t("no_city")},{" "}
                  {post.sellerCountry || t("no_country")}
                </div>
              </div>

              {post.images?.[0] && (
                <img
                  src={post.images[0]}
                  className="post-single-image"
                  alt="Product"
                />
              )}

              <div
                className="post-footer"
                style={{ borderTop: "none", paddingTop: 0 }}
              >
                <div className="reactions">
                  <Heart size={18} color="#ff4757" fill="#ff4757" />
                  <span style={{ marginLeft: "4px" }}>
                    {Number(post.likes || 0)}
                  </span>

                  <MessageCircle
                    size={18}
                    color="#70a1ff"
                    style={{ marginLeft: "15px" }}
                  />
                  <span style={{ marginLeft: "4px" }}>
                    {Number(post.comments || 0)}
                  </span>
                </div>

                <div className="footer-actions">
                  <button
                    onClick={() => handleBuyNow(post)}
                    style={{
                      background: "linear-gradient(90deg, #ff4d79 0%, #8a2be2 100%)",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      boxShadow: "0 4px 10px rgba(138, 43, 226, 0.3)",
                    }}
                  >
                    {t("buy_now")}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div
        className="fab-button"
        style={{ position: "fixed", bottom: "30px", right: "30px", top: "auto" }}
      >
        <PlusCircle size={28} />
      </div>
    </div>
  );
};

export default Market;